import { Plane, Instagram, Facebook, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { AGENCY, waLink, DEFAULT_WA_MESSAGE } from "@/config";

const SERVICE_LINKS = [
  ["Passagens Aéreas", "#servicos"],
  ["Envios DHL", "#servicos"],
  ["Loja de Roupas", "#servicos"],
  ["Western Union", "#servicos"],
  ["Naturalização", "#servicos"],
];

const scrollTo = (e, href) => {
  e.preventDefault();
  document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export const Footer = () => (
  <footer data-testid="footer" className="relative bg-navy-dark grain overflow-hidden">
    <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pt-20 pb-10 relative z-10">
      <motion.h2
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        data-testid="footer-brand-title"
        className="font-serif font-bold leading-none text-[11vw] lg:text-[8vw] text-transparent bg-clip-text bg-gradient-to-b from-gold/40 to-gold/5 whitespace-nowrap select-none"
      >
        A.S.F VIAGENS
      </motion.h2>

      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-9 h-9 rounded-full bg-gold flex items-center justify-center">
              <Plane className="w-4 h-4 text-navy" strokeWidth={2.4} />
            </span>
            <span className="font-serif text-xl font-bold text-ivory">A.S.F Viagens</span>
          </div>
          <p className="text-sm text-ivory/50 leading-relaxed">
            Sua ponte para o mundo: passagens, câmbio, envios internacionais,
            moda e cidadania, com atendimento humanizado em tempo real.
          </p>
          <div className="flex gap-3 mt-6">
            {[
              [Instagram, "footer-social-instagram"],
              [Facebook, "footer-social-facebook"],
              [MessageCircle, "footer-social-whatsapp"],
            ].map(([Icon, id]) => (
              <a
                key={id}
                href={id === "footer-social-whatsapp" ? waLink(DEFAULT_WA_MESSAGE) : "#contato"}
                onClick={id === "footer-social-whatsapp" ? undefined : (e) => scrollTo(e, "#contato")}
                target={id === "footer-social-whatsapp" ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid={id}
                className="w-10 h-10 rounded-full border border-ivory/15 flex items-center justify-center text-ivory/60 hover:text-gold hover:border-gold/50 transition-all duration-300"
                aria-label={id}
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold mb-5">
            Serviços
          </p>
          <ul className="space-y-3">
            {SERVICE_LINKS.map(([label, href]) => (
              <li key={label}>
                <a
                  href={href}
                  onClick={(e) => scrollTo(e, href)}
                  data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm text-ivory/60 hover:text-gold transition-colors"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold mb-5">
            Navegação
          </p>
          <ul className="space-y-3">
            {[
              ["Galeria", "#galeria"],
              ["Depoimentos", "#depoimentos"],
              ["Cotação", "#cotacao"],
              ["Contato", "#contato"],
            ].map(([label, href]) => (
              <li key={label}>
                <a
                  href={href}
                  onClick={(e) => scrollTo(e, href)}
                  data-testid={`footer-nav-${label.toLowerCase()}`}
                  className="text-sm text-ivory/60 hover:text-gold transition-colors"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold mb-5">
            Contato
          </p>
          <p className="text-sm text-ivory/60 leading-relaxed">{AGENCY.address}</p>
          <p className="text-sm text-ivory/60 mt-3">{AGENCY.phoneDisplay}</p>
          <p className="text-sm text-ivory/60 mt-2">{AGENCY.email}</p>
          <p className="text-sm text-ivory/40 mt-3">{AGENCY.hours}</p>
          <p className="font-mono text-[11px] text-ivory/35 mt-4 tracking-wider">
            CNPJ: {AGENCY.cnpj}
          </p>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t border-ivory/10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-ivory/40">
          © 2026 A.S.F Viagens. Todos os direitos reservados.
        </p>
        <p className="font-mono text-[11px] text-gold/60 tracking-[0.2em]">
          {AGENCY.domain}
        </p>
      </div>
    </div>
  </footer>
);
